
export interface Task {
  id: string; title: string; urgency: number; importance: number; impact: number;
  confidence: number; effort: number; reach: number; notes: string; deadline?: string;
}

export interface ScoredTask extends Task {
  eisenhower: { quadrant: string; score: number };
  rice: { score: number; breakdown: any };
  moscow: string;
  combinedScore: number;
  rank: number;
  timeBlock: string;
  reasoning: string;
  color: string;
}

function eisenhowerScore(task: Task) {
  const urgent = task.urgency || 3;
  const important = task.importance || 3;
  let quadrant: string;
  if (urgent >= 4 && important >= 4) quadrant = 'DO_FIRST';
  else if (urgent < 4 && important >= 4) quadrant = 'SCHEDULE';
  else if (urgent >= 4 && important < 4) quadrant = 'DELEGATE';
  else quadrant = 'ELIMINATE';
  return { quadrant, score: (urgent + important) / 2 };
}

function riceScore(task: Task) {
  const reach = task.reach || 5;
  const impact = task.impact || 1;
  const confidence = task.confidence || 80;
  const effort = task.effort || 5;
  const score = (reach * impact * (confidence / 100)) / effort;
  return { score: +score.toFixed(2), breakdown: { reach, impact, confidence, effort } };
}

function moscowClassify(task: Task) {
  const mustKeywords = ['compliance', 'legal', 'deadline', 'critical', 'blocker', 'security', 'payment', 'contract'];
  const shouldKeywords = ['important', 'valuable', 'expected', 'standard', 'quality', 'performance'];
  const couldKeywords = ['nice', 'if time', 'enhance', 'improve', 'optimize', 'polish'];
  const text = (task.title + ' ' + (task.notes || '')).toLowerCase();
  if (mustKeywords.some(kw => text.includes(kw))) return 'MUST_HAVE';
  if (shouldKeywords.some(kw => text.includes(kw))) return 'SHOULD_HAVE';
  if (couldKeywords.some(kw => text.includes(kw))) return 'COULD_HAVE';
  return 'WONT_HAVE';
}

function suggestTimeBlock(effort: number, quadrant: string) {
  if (quadrant === 'DO_FIRST') {
    if (effort <= 2) return 'Morning Deep Work (9:00-11:00)';
    if (effort <= 4) return 'Morning Focus Block (11:00-13:00)';
    return 'Full Day Priority — Start Immediately';
  }
  if (quadrant === 'SCHEDULE') return 'Afternoon Planned Work (14:00-17:00)';
  if (quadrant === 'DELEGATE') return 'Delegate Before 10:00 AM';
  return 'Eliminate or Defer Indefinitely';
}

function generateReasoning(task: Task, e: any, r: any, m: string) {
  const reasons: string[] = [];
  if (e.quadrant === 'DO_FIRST') reasons.push('Urgent AND important — critical priority');
  if (e.quadrant === 'SCHEDULE') reasons.push('Important but not urgent — plan focused time');
  if (r.score > 5) reasons.push(`High RICE score (${r.score}) — high impact, low effort`);
  if (r.score < 2) reasons.push(`Low RICE score (${r.score}) — consider if worth doing`);
  if (m === 'MUST_HAVE') reasons.push('Must-have — non-negotiable');
  if (m === 'WONT_HAVE') reasons.push("Won't-have — lowest priority, eliminate if possible");
  return reasons.join('. ') || 'Standard priority task';
}

export function prioritizeTasks(tasks: Task[]): ScoredTask[] {
  return tasks.map(task => {
    const e = eisenhowerScore(task);
    const r = riceScore(task);
    const m = moscowClassify(task);
    const moscowScore = m === 'MUST_HAVE' ? 10 : m === 'SHOULD_HAVE' ? 7 : m === 'COULD_HAVE' ? 4 : 1;
    const combined = (e.score * 0.3) + (r.score * 0.4) + (moscowScore * 0.3);
    return {
      ...task,
      eisenhower: e,
      rice: r,
      moscow: m,
      combinedScore: +combined.toFixed(2),
      rank: 0,
      timeBlock: suggestTimeBlock(task.effort, e.quadrant),
      reasoning: generateReasoning(task, e, r, m),
      color: e.quadrant === 'DO_FIRST' ? '#ef4444' : e.quadrant === 'SCHEDULE' ? '#f59e0b' : e.quadrant === 'DELEGATE' ? '#3b82f6' : '#6b7280'
    };
  }).sort((a, b) => b.combinedScore - a.combinedScore).map((t, i) => ({ ...t, rank: i + 1 }));
}

export function exportICS(tasks: ScoredTask[]) {
  let ics = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//CatalystAgile//PriorityOne//EN\n';
  const now = new Date();
  tasks.filter(t => t.eisenhower.quadrant === 'DO_FIRST' || t.eisenhower.quadrant === 'SCHEDULE').slice(0, 5).forEach((task, i) => {
    const start = new Date(now);
    start.setDate(start.getDate() + i);
    start.setHours(9 + i * 2, 0, 0);
    const end = new Date(start);
    end.setHours(end.getHours() + (task.effort || 2));
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    ics += `BEGIN:VEVENT\nUID:${task.id}@catalystagile\nDTSTART:${fmt(start)}\nDTEND:${fmt(end)}\nSUMMARY:${task.title}\nDESCRIPTION:${task.reasoning}\nEND:VEVENT\n`;
  });
  ics += 'END:VCALENDAR';
  return ics;
}
